/**
 * Remembering where the user opened an event FROM, so a delete can send them
 * back there.
 *
 * The event page's breadcrumb answers a different question — where the event
 * BELONGS (its competition, or its team). Those two diverge the moment a
 * competition event is opened from a team's schedule: the breadcrumb should
 * still say "competition", but a delete should return to the team list the user
 * was actually reading.
 *
 * Carried in React Router's location state rather than a query param: it is
 * navigational bookkeeping, not part of the event's address, and it must not
 * end up in a shared URL. History state survives a refresh, so it holds for as
 * long as the entry does; a link opened in a new tab or pasted in carries none,
 * which is exactly when the fallback is right.
 */

/** Location state attached to every in-app link to an event page. */
export interface EventLinkState {
  /** Absolute in-app path (with query) the user came from. */
  from?: string
}

/** Minimal shape of a React Router location — typed here so the helpers can be
 *  unit tested without pulling in the router. */
interface LocationLike {
  pathname: string
  search?: string
}

/**
 * The state to hang on a `<Link>` to an event page.
 *
 * Reads the CURRENT location, so a shared row component records whichever list
 * it happens to be rendered in without every caller passing it down.
 */
export const buildEventLinkState = (
  location: LocationLike
): EventLinkState => ({
  from: `${location.pathname}${location.search ?? ''}`,
})

/**
 * Where a delete should land: where the user came from, else the event's own
 * owner.
 *
 * `fallback` covers every entry that carries no state — a pasted link, a new
 * tab, a bookmark, an external referrer — which is the case the caller has to
 * answer for anyway.
 *
 * Only in-app absolute paths are honoured. The state is written by our own
 * links so a foreign value should be impossible, but a redirect target is worth
 * validating rather than trusting: anything else falls back.
 */
export const resolveEventReturnTo = (
  state: EventLinkState | null | undefined,
  fallback: string
): string => {
  const from = state?.from
  if (typeof from !== 'string') {
    return fallback
  }
  // A single leading slash: rejects '' and protocol-relative '//evil.example',
  // which the browser would treat as an absolute URL to another host.
  if (!from.startsWith('/') || from.startsWith('//')) {
    return fallback
  }
  return from
}
